/**
 * Meal Planning Service
 * Orchestrates meal planning conversations via backend API
 */

import backendApiService from './backendApiService'
import { PersistentPreferences, SessionPreferences } from '../types/preferences'
import { MealPlanningSession, MealPlanResponse } from '../types'
import { clientLogger } from '../utils/clientLogger'

class MealPlanningService {
  private activeSessions = new Map<string, MealPlanningSession>()

  /**
   * Start a new meal planning session via backend API
   */
  async createSession(
    userId: string,
    mealSessionId: string,
    persistentPreferences: PersistentPreferences,
    sessionPreferences: SessionPreferences
  ): Promise<MealPlanningSession> {
    clientLogger.debug({ userId, mealSessionId, persistentPreferencesCount: Object.keys(persistentPreferences).length, sessionPreferencesCount: Object.keys(sessionPreferences).length }, 'Creating session via backend API')

    const backendSession = await backendApiService.createSession(
      userId,
      mealSessionId,
      persistentPreferences,
      sessionPreferences
    )

    // Convert backend session to frontend format
    const session: MealPlanningSession = {
      sessionId: backendSession.sessionId,
      userId: backendSession.userId,
      startTime: backendSession.startTime,
      lastActivity: backendSession.lastActivity,
      persistentPreferences: backendSession.persistentPreferences,
      sessionPreferences: backendSession.sessionPreferences,
      conversationHistory: backendSession.conversationHistory,
      currentMeals: backendSession.currentMeals,
      isComplete: backendSession.isComplete
    }

    // Store in memory for immediate access
    this.activeSessions.set(session.sessionId, session)

    clientLogger.debug({ sessionId: session.sessionId }, 'Session created')
    return session
  }

  /**
   * Get existing session (checks memory first, then backend)
   */
  async getSession(sessionId: string, userId?: string): Promise<MealPlanningSession | null> {
    // Check memory first for performance
    const memorySession = this.activeSessions.get(sessionId)
    if (memorySession) {
      return memorySession
    }

    // Try to restore from backend
    if (userId) {
      clientLogger.debug({ sessionId }, 'Fetching session from backend')
      const backendSession = await backendApiService.getSession(sessionId, userId)
      if (backendSession) {
        // Convert to frontend format and cache in memory
        const session: MealPlanningSession = {
          sessionId: backendSession.sessionId,
          userId: backendSession.userId,
          startTime: backendSession.startTime,
          lastActivity: backendSession.lastActivity,
          persistentPreferences: backendSession.persistentPreferences,
          sessionPreferences: backendSession.sessionPreferences,
          conversationHistory: backendSession.conversationHistory,
          currentMeals: backendSession.currentMeals,
          isComplete: backendSession.isComplete
        }
        this.activeSessions.set(sessionId, session)
        clientLogger.debug({}, 'Session fetched from backend')
        return session
      }
    }

    return null
  }

  /**
   * Get existing session synchronously (memory only, for backward compatibility)
   */
  getSessionSync(sessionId: string): MealPlanningSession | null {
    return this.activeSessions.get(sessionId) || null
  }

  /**
   * Process user message and generate meal planning response via backend API
   */
  async processMessage(
    sessionId: string,
    message: string,
    userId: string,
    metadata?: { recipeToReplace?: string; clearHistory?: boolean }
  ): Promise<MealPlanResponse> {
    clientLogger.debug({ sessionId, userId, messagePreview: message.substring(0, 50) + '...', metadata }, 'Processing message via backend API')

    const backendResponse = await backendApiService.sendMessage(sessionId, userId, message, metadata)

    // Update local session cache with backend session
    const updatedSession: MealPlanningSession = {
      sessionId: backendResponse.session.sessionId,
      userId: backendResponse.session.userId,
      startTime: backendResponse.session.startTime,
      lastActivity: backendResponse.session.lastActivity,
      persistentPreferences: backendResponse.session.persistentPreferences,
      sessionPreferences: backendResponse.session.sessionPreferences,
      conversationHistory: backendResponse.session.conversationHistory,
      currentMeals: backendResponse.session.currentMeals,
      isComplete: backendResponse.session.isComplete
    }
    this.activeSessions.set(sessionId, updatedSession)

    // Convert backend response to frontend format
    const response: MealPlanResponse = {
      response: backendResponse.response,
      recipes: backendResponse.recipes,
      sessionId: backendResponse.sessionId,
      suggestions: backendResponse.suggestions,
      conversationComplete: backendResponse.conversationComplete,
      // Forward rerollTracking so the caller can update the reroll counter
      // immediately without waiting for a full subscription refresh
      rerollTracking: backendResponse.rerollTracking ?? null,
      // Forward reroll source metadata so the frontend can skip polling for DB hits
      rerollSource: backendResponse.rerollSource,
      noMatch: backendResponse.noMatch
    }

    clientLogger.debug({ sessionId: response.sessionId, responseLength: response.response?.length ?? 0, recipesCount: response.recipes?.length || 0 }, 'Message processing successful')

    return response
  }

  /**
   * Handle user feedback (thumbs up/down, re-roll, etc.)
   */
  async handleFeedback(
    sessionId: string,
    feedbackType: 'thumbs_up' | 'thumbs_down' | 'reroll' | 'modify',
    targetItem?: string,
    modifications?: string
  ): Promise<MealPlanResponse> {
    const session = this.getSessionSync(sessionId)
    if (!session) {
      throw new Error('Session not found')
    }

    let feedbackMessage = ''
    
    switch (feedbackType) {
      case 'thumbs_up':
        feedbackMessage = targetItem 
          ? `I like the ${targetItem}. Keep it and show me more options like this.`
          : 'I like these suggestions. Please continue with similar options.'
        break
      
      case 'thumbs_down':
        feedbackMessage = targetItem
          ? `I don't like the ${targetItem}. Please suggest something different.`
          : 'I don\'t like these suggestions. Please try different options.'
        break
      
      case 'reroll':
        feedbackMessage = 'Please give me completely different meal options.'
        break
      
      case 'modify':
        feedbackMessage = modifications || 'Please modify the suggestions based on my feedback.'
        break
    }

    return this.processMessage(sessionId, feedbackMessage, session.userId)
  }

  /**
   * Get session summary and final meal plan
   */
  async getSessionSummary(sessionId: string, userId: string): Promise<{
    finalMealPlan: any[]
    conversationSummary: string
    preferences: any
    duration: number
  } | null> {
    const session = await this.getSession(sessionId, userId)
    if (!session) return null

    const duration = new Date().getTime() - new Date(session.startTime).getTime()

    return {
      finalMealPlan: session.currentMeals,
      conversationSummary: `Meal planning session completed with ${session.conversationHistory.length} messages over ${Math.round(duration / 1000 / 60)} minutes.`,
      preferences: {
        persistent: session.persistentPreferences,
        session: session.sessionPreferences
      },
      duration: Math.round(duration / 1000 / 60) // minutes
    }
  }

  /**
   * Clean up inactive sessions (older than 1 hour)
   */
  cleanupInactiveSessions(): void {
    const oneHourAgo = new Date().getTime() - (60 * 60 * 1000)
    
    for (const [sessionId, session] of this.activeSessions.entries()) {
      const lastActivity = new Date(session.lastActivity).getTime()
      if (lastActivity < oneHourAgo) {
        this.activeSessions.delete(sessionId)
        clientLogger.debug({ sessionId }, 'Cleaned up inactive session')
      }
    }
  }

  /**
   * Get all active sessions for a user
   */
  getUserSessions(userId: string): MealPlanningSession[] {
    return Array.from(this.activeSessions.values())
      .filter(session => session.userId === userId)
  }

  /**
   * Export session data for email/sharing
   */
  async exportSessionData(sessionId: string, userId: string): Promise<{
    recipes: any[]
    shoppingList: string[]
    mealPlan: string
  } | null> {
    const session = await this.getSession(sessionId, userId)
    if (!session || !session.isComplete) return null

    // Generate shopping list from recipes
    const shoppingList = this.generateShoppingList(session.currentMeals)

    // Generate formatted meal plan
    const mealPlan = this.formatMealPlan(session.currentMeals, session.sessionPreferences)

    return {
      recipes: session.currentMeals,
      shoppingList,
      mealPlan
    }
  }

  /**
   * Generate consolidated shopping list from recipes
   */
  private generateShoppingList(recipes: any[]): string[] {
    const ingredientMap = new Map<string, { amount: number; unit: string; category: string }>()

    recipes.forEach(recipe => {
      recipe.ingredients?.forEach((ingredient: any) => {
        const key = ingredient.name.toLowerCase()
        if (ingredientMap.has(key)) {
          const existing = ingredientMap.get(key)!
          // Simple addition - in production would need unit conversion
          existing.amount += ingredient.amount
        } else {
          ingredientMap.set(key, {
            amount: ingredient.amount,
            unit: ingredient.unit,
            category: ingredient.category || 'Other'
          })
        }
      })
    })

    return Array.from(ingredientMap.entries()).map(([name, details]) => 
      `${details.amount} ${details.unit} ${name}`
    )
  }

  /**
   * Format meal plan for display/export
   */
  private formatMealPlan(recipes: any[], session: SessionPreferences): string {
    const header = `Meal Plan for ${session.numberOfPeople} people\n`
    const enabledMealTypes = session.mealTypes
      .filter((meal: { enabled: boolean; type: string }) => meal.enabled)
      .map((meal: { enabled: boolean; type: string }) => meal.type)
      .join(', ')
    
    let formatted = `${header}Meal Types: ${enabledMealTypes}\n\n`
    
    recipes.forEach((recipe, index) => {
      formatted += `${index + 1}. ${recipe.name}\n`
      formatted += `   Prep: ${recipe.prepTime}min | Cook: ${recipe.cookTime}min | Serves: ${recipe.servings}\n`
      formatted += `   ${recipe.description}\n\n`
    })

    return formatted
  }


  /**
   * Health check for meal planning service
   */
  async healthCheck(): Promise<{
    backend: boolean
    activeSessions: number
  }> {
    const backendHealth = await backendApiService.testConnectivity()

    return {
      backend: backendHealth,
      activeSessions: this.activeSessions.size
    }
  }
}

export const mealPlanningService = new MealPlanningService()

// Cleanup inactive sessions every 30 minutes
setInterval(() => {
  mealPlanningService.cleanupInactiveSessions()
}, 30 * 60 * 1000)

export default mealPlanningService